/**
 * Product margin categories (Product.marginCategory) — A = Low, B = Moderate,
 * C = High margin. Display only: used by the header "Product Categories"
 * dialog and to group rows under coloured category headers in Sheet Order
 * Upload and Create Bulk Order. Data: GET /api/ai/products/margin-categories.
 * Shailendra, 2026-09-30.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TableRow, TableCell } from '@material-ui/core';

const AI_PATH = (process.env.REACT_APP_AI_PATH || '').trim();

export const MARGIN_CATEGORIES = {
  A: { key: 'A', label: 'Low Margin',      bg: '#d32f2f', color: '#fff',    rowTint: '#fdecea' },
  B: { key: 'B', label: 'Moderate Margin', bg: '#f57c00', color: '#fff',    rowTint: '#fff3e0' },
  C: { key: 'C', label: 'High Margin',     bg: '#a5d6a7', color: '#1b5e20', rowTint: '#f1f8e9' },
};
// Order tables (Sheet Upload / Create Bulk Order): Low → Moderate → High.
export const MARGIN_CATEGORY_ORDER = ['A', 'B', 'C'];
// Header "Product Categories" dialog: High → Moderate → Low.
export const MARGIN_CATEGORY_ORDER_HIGH_FIRST = ['C', 'B', 'A'];
const OTHER = { key: 'OTHER', label: 'Uncategorised', bg: '#e0e0e0', color: '#424242', rowTint: 'transparent' };

// Module-level cache — the list is small and changes rarely, so every table
// on the page shares one fetch per page load.
let _cache = null;
let _inflight = null;

export function fetchMarginCategoryProducts() {
  if (_cache) return Promise.resolve(_cache);
  if (!_inflight) {
    _inflight = axios.get(`${AI_PATH}/api/ai/products/margin-categories`)
      .then(res => { _cache = res.data?.products || []; return _cache; })
      .catch(() => [])
      .finally(() => { _inflight = null; });
  }
  return _inflight;
}

/** productId (string) -> 'A' | 'B' | 'C' */
export function useMarginCategoryMap() {
  const [map, setMap] = useState(() => (_cache ? _toMap(_cache) : new Map()));
  useEffect(() => {
    let alive = true;
    fetchMarginCategoryProducts().then(list => { if (alive) setMap(_toMap(list)); });
    return () => { alive = false; };
  }, []);
  return map;
}

function _toMap(list) {
  return new Map((list || []).map(p => [String(p._id), p.marginCategory]));
}

/**
 * Orders `items` by `order` (default A → B → C), then uncategorised, and
 * interleaves a header entry
 * before each group. Every item keeps its ORIGINAL index (`ind`) so callers
 * whose handlers are index-based (qty change / remove) keep working.
 * Returns [{ header: cat } | { item, ind }]. If no item is categorised, the
 * list comes back unchanged with no headers.
 */
export function groupByMarginCategory(items, getId, catMap, order = MARGIN_CATEGORY_ORDER) {
  const entries = (items || []).map((item, ind) => ({ item, ind, cat: catMap.get(String(getId(item) || '')) || null }));
  if (!entries.some(e => e.cat)) return entries.map(({ item, ind }) => ({ item, ind }));
  const out = [];
  for (const key of [...order, null]) {
    const group = entries.filter(e => e.cat === key);
    if (!group.length) continue;
    out.push({ header: key ? MARGIN_CATEGORIES[key] : OTHER, count: group.length });
    group.forEach(({ item, ind }) => out.push({ item, ind }));
  }
  return out;
}

export function MarginCategoryHeaderRow({ cat, count, colSpan }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} style={{ background: cat.bg, color: cat.color, fontWeight: 700, fontSize: 13, padding: '6px 12px' }}>
        {cat.key === 'OTHER' ? `${cat.label} Products` : `Category ${cat.key} - ${cat.label} Products`}
        {count != null && <span style={{ fontWeight: 400, marginLeft: 8, opacity: 0.85 }}>({count})</span>}
      </TableCell>
    </TableRow>
  );
}

const MARGIN_BANDS = {
  low:    { label: 'Low Margin',    bg: '#d32f2f', color: '#fff' },
  medium: { label: 'Medium Margin', bg: '#f57c00', color: '#fff' },
  high:   { label: 'High Margin',   bg: '#2e7d32', color: '#fff' },
};

const _inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

/**
 * Overall order margin strip shown above the product table — colour + label
 * only (the actual % is never sent to agents). `band` comes from the backend
 * (orderSheetParser _marginBand): red < 15%, orange below the agent's high
 * threshold (30% cohort / 35% non-cohort), green at/above it. Independently,
 * if Category C products are under 20% of order value, shows how much Category
 * C to add (backend-computed).
 */
export function MarginBandStrip({ band, style }) {
  if (!band || !MARGIN_BANDS[band.level]) return null;
  const b = MARGIN_BANDS[band.level];
  const s = band.suggestion;
  return (
    <div style={{ background: b.bg, color: b.color, borderRadius: 4, padding: '8px 14px', marginBottom: 10, fontSize: 13, ...style }}>
      <b style={{ fontSize: 14 }}>Order Margin: {b.label}</b>
      {s && s.addAmount > 0 && (
        <div style={{ marginTop: 4 }}>
          Add about <b>{_inr(s.addAmount)}</b> of <b>Category C (High Margin)</b> products.
        </div>
      )}
    </div>
  );
}
